import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ArrowRight, Activity, LayoutGrid, BarChart3, Brain, ShieldCheck, Gauge, GraduationCap } from "lucide-react";
import { useSeo, JsonLd, SITE_URL, PERSON_ID } from "@/lib/seo";
import { MODULE_SYSTEMS, MODULES, modulesBySystem, isFree } from "@/data/modules";
import { TerminalWorkspace } from "@/components/landing/TerminalWorkspace";
import { SignalScanner } from "@/components/landing/SignalScanner";
import { TerminalTour } from "@/components/landing/TerminalTour";
import { Footer } from "@/components/landing/Footer";

const IMAGE = `${SITE_URL}/tq-logo.png`;
const PAGE_URL = `${SITE_URL}/terminal`;
const SYS_ICON = { engine: Activity, discovery: LayoutGrid, data: BarChart3, intel: Brain, verify: ShieldCheck, risk: Gauge, edu: GraduationCap };

const schema = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "The TradrQuant Terminal — Modules",
    description:
      "Every module inside the TradrQuant professional trading terminal — the market scanner, GEX terminal, whale radar, order flow, liquidation map, risk controls, verification and more.",
    url: PAGE_URL,
    inLanguage: "en",
    isPartOf: { "@type": "WebSite", name: "TradrQuant", url: SITE_URL },
    author: { "@type": "Person", "@id": PERSON_ID, name: "JC Rod", alternateName: "Tradernaut" },
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: MODULES.map((m, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: m.name,
      url: `${SITE_URL}/terminal/${m.slug}`,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "The Terminal", item: PAGE_URL },
    ],
  },
];

export default function TerminalHub() {
  useSeo({
    title: "The TradrQuant Terminal — Every Module | Professional Crypto Trading Terminal",
    description:
      "Explore every module inside TradrQuant — the professional crypto trading terminal built for retail. Market scanner, GEX terminal, whale radar, order flow, liquidation map, risk controls and public verification.",
    path: "/terminal",
  });
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const t = setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="terminal-hub" className="grain relative min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <JsonLd data={schema} />

      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto group-hover:scale-105 transition-transform duration-300" />
            <span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[19px] sm:text-[21px]">Tradr<span className="tq-q">Q</span>uant</span>
          </Link>
          <Link to="/" data-testid="hub-back-btn" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 border border-white/15 px-3 py-2 hover:text-[#BACD1C] hover:border-[#BACD1C]/50 transition-colors duration-300">
            <ChevronLeft size={14} /> cd ~/home
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-[1100px] px-5 md:px-10 pt-12 md:pt-16 pb-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#7D8390]">// the terminal · {MODULES.length} modules</p>
        <h1 className="mt-4 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl leading-[0.98]">
          Every desk a fund runs.<span className="block text-[#BACD1C]">One terminal, built for retail.</span>
        </h1>
        <p className="mt-6 font-term text-[15px] text-white/60 leading-relaxed max-w-2xl">
          The complete map of the TradrQuant terminal — screening, order flow, options gamma, whale intel,
          risk controls and a publicly verified track record. Explore any module below.
        </p>

        <div className="mt-14 space-y-14">
          {MODULE_SYSTEMS.map((sys) => {
            const Icon = SYS_ICON[sys.key] || Activity;
            const mods = modulesBySystem(sys.key);
            return (
              <section key={sys.key} id={sys.key} data-testid={`hub-system-${sys.key}`} className="scroll-mt-24">
                <div className="flex items-center gap-2.5 border-b border-white/10 pb-3">
                  <Icon size={16} className="text-[#BACD1C]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7D8390]">{sys.name}</span>
                  <span className="h-px flex-1 bg-gradient-to-r from-[#BACD1C]/30 to-transparent" />
                  <span className="font-mono text-[10px] text-white/30">{mods.length}</span>
                </div>
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {mods.map((m, idx) => (
                    <motion.div
                      key={m.slug}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.5, delay: (idx % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <Link to={`/terminal/${m.slug}`} data-testid={`hub-module-${m.slug}`}
                        className="group block h-full border border-white/[0.08] bg-[#13151A] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.05)] hover:bg-[#1A1D24] hover:border-[#BACD1C]/30 p-5 transition-colors duration-300">
                        <div className="flex items-start justify-between gap-2">
                          <h2 className="font-display font-semibold tracking-[-0.03em] text-[15px] leading-tight text-white/90 group-hover:text-white">{m.name}</h2>
                          <ArrowRight size={15} className="mt-0.5 shrink-0 text-white/25 group-hover:text-[#BACD1C] group-hover:translate-x-0.5 transition-[color,transform] duration-300" />
                        </div>
                        <span className={`mt-2.5 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 border ${isFree(m.slug) ? "border-white/[0.10] text-[#7D8390] bg-white/[0.03]" : "border-white/12 text-white/40"}`}>
                          <span className={`h-1 w-1 rounded-full ${isFree(m.slug) ? "bg-[#BACD1C]" : "bg-white/30"}`} />
                          {isFree(m.slug) ? "Free today" : "In terminal"}
                        </span>
                        <p className="mt-2.5 font-mono text-[12px] text-white/50 leading-relaxed">{m.tagline}</p>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
      <TerminalTour />
      <TerminalWorkspace />
      <SignalScanner />
      <Footer />
    </div>
  );
}
